import { ReadonlyVec3, vec3 } from 'gl-matrix';

export type DvfGeometry = {
    size: [number, number, number];
    spacing: [number, number, number];
    origin: [number, number, number];
    direction: number[];
};

export type DvfField = DvfGeometry & {
    data: Float32Array;
    metrics?: DvfModelGridMetrics;
};

export interface DvfModelGridMetrics {
    readonly minJacobian: number;
    readonly maxJacobian: number;
    readonly foldingFraction: number;
    readonly measuredSize: readonly [number, number, number];
}

const IDENTITY_DIRECTION = [1, 0, 0, 0, 1, 0, 0, 0, 1];

function isIdentityDirection(direction: number[]): boolean {
    return (
        direction.length === 9 &&
        direction.every((v, i) => Math.abs(v - IDENTITY_DIRECTION[i]) < 1e-6)
    );
}

function axisPermutation(direction: number[]): {
    worldAxis: [number, number, number];
    sign: [number, number, number];
} | null {
    if (direction.length !== 9) {
        return null;
    }
    const worldAxis: number[] = [];
    const sign: number[] = [];
    for (let axis = 0; axis < 3; axis++) {
        const column = [
            direction[axis],
            direction[3 + axis],
            direction[6 + axis],
        ];
        const nonZero = column
            .map((v, row) => ({ v, row }))
            .filter(({ v }) => Math.abs(v) > 1e-6);
        if (
            nonZero.length !== 1 ||
            Math.abs(Math.abs(nonZero[0].v) - 1) > 1e-6
        ) {
            return null;
        }
        worldAxis.push(nonZero[0].row);
        sign.push(Math.sign(nonZero[0].v));
    }
    if (new Set(worldAxis).size !== 3) {
        return null;
    }
    return {
        worldAxis: worldAxis as [number, number, number],
        sign: sign as [number, number, number],
    };
}

function canonicaliseAxisAligned(
    data: Float32Array,
    geometry: DvfGeometry,
    permutation: {
        worldAxis: [number, number, number];
        sign: [number, number, number];
    }
): DvfField {
    const { worldAxis, sign } = permutation;
    const size = geometry.size;
    const newSize: [number, number, number] = [0, 0, 0];
    const newSpacing: [number, number, number] = [0, 0, 0];
    const newOrigin: [number, number, number] = [0, 0, 0];
    for (let axis = 0; axis < 3; axis++) {
        const world = worldAxis[axis];
        newSize[world] = size[axis];
        newSpacing[world] = geometry.spacing[axis];
        // Negative axis direction: voxel 0 of the new grid is the far end of the old one.
        newOrigin[world] =
            sign[axis] > 0
                ? geometry.origin[world]
                : geometry.origin[world] -
                  geometry.spacing[axis] * (size[axis] - 1);
    }

    const out = new Float32Array(data.length);
    const [n0, n1] = size;
    const [m0, m1] = newSize;
    const destIndex = [0, 0, 0];
    for (let i2 = 0; i2 < size[2]; i2++) {
        for (let i1 = 0; i1 < size[1]; i1++) {
            for (let i0 = 0; i0 < size[0]; i0++) {
                const source = [i0, i1, i2];
                for (let axis = 0; axis < 3; axis++) {
                    destIndex[worldAxis[axis]] =
                        sign[axis] > 0
                            ? source[axis]
                            : size[axis] - 1 - source[axis];
                }
                const from = ((i2 * n1 + i1) * n0 + i0) * 3;
                const to =
                    ((destIndex[2] * m1 + destIndex[1]) * m0 + destIndex[0]) *
                    3;
                out[to] = data[from];
                out[to + 1] = data[from + 1];
                out[to + 2] = data[from + 2];
            }
        }
    }
    return {
        size: newSize,
        spacing: newSpacing,
        origin: newOrigin,
        direction: [...IDENTITY_DIRECTION],
        data: out,
    };
}

export function parseDvfField(
    buffer: ArrayBuffer,
    geometry: DvfGeometry
): DvfField {
    const [nx, ny, nz] = geometry.size;
    const expected = nx * ny * nz * 3;
    const data = new Float32Array(buffer);
    if (data.length !== expected) {
        throw new Error(
            `DVF size mismatch: got ${data.length} floats, expected ${expected} ` +
                `for grid ${nx}x${ny}x${nz}x3`
        );
    }
    const [sx, sy, sz] = geometry.spacing;
    if (!(sx > 0 && sy > 0 && sz > 0)) {
        throw new Error(
            `DVF spacing must be positive mm, got [${sx}, ${sy}, ${sz}]`
        );
    }
    if (isIdentityDirection(geometry.direction)) {
        return { ...geometry, data };
    }
    const permutation = axisPermutation(geometry.direction);
    if (!permutation) {
        throw new Error(
            'DVF direction must be axis-aligned (each grid axis parallel to a patient ' +
                `axis), got [${geometry.direction.join(', ')}]`
        );
    }
    console.debug(
        `DVF grid is axis-aligned but not identity ([${geometry.direction.join(', ')}]); ` +
            're-indexing onto an identity-direction grid.'
    );
    return canonicaliseAxisAligned(data, geometry, permutation);
}

function flatIndex(field: DvfField, x: number, y: number, z: number): number {
    const [nx, ny] = field.size;
    return ((z * ny + y) * nx + x) * 3;
}

export function dvfVoxelToPatient(
    field: DvfGeometry,
    x: number,
    y: number,
    z: number
): vec3 {
    const [sx, sy, sz] = field.spacing;
    const [ox, oy, oz] = field.origin;
    // Canonical grid has identity direction, so patient mm = origin + spacing ⊙ voxel.
    return vec3.fromValues(ox + sx * x, oy + sy * y, oz + sz * z);
}

export function dvfSampleNearest(
    field: DvfField,
    x: number,
    y: number,
    z: number
): vec3 {
    const i = flatIndex(field, x, y, z);
    return vec3.fromValues(field.data[i], field.data[i + 1], field.data[i + 2]);
}

export function dvfDisplacementAt(
    field: DvfField,
    point: ReadonlyVec3
): vec3 | undefined {
    const out = vec3.create();
    return dvfDisplacementInto(field, point, out) ? out : undefined;
}

export function dvfDisplacementInto(
    field: DvfField,
    point: ReadonlyVec3,
    out: vec3
): boolean {
    const [nx, ny, nz] = field.size;
    const fx = (point[0] - field.origin[0]) / field.spacing[0];
    const fy = (point[1] - field.origin[1]) / field.spacing[1];
    const fz = (point[2] - field.origin[2]) / field.spacing[2];
    if (
        fx < 0 ||
        fy < 0 ||
        fz < 0 ||
        fx > nx - 1 ||
        fy > ny - 1 ||
        fz > nz - 1
    ) {
        return false;
    }

    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const z0 = Math.floor(fz);
    const x1 = Math.min(x0 + 1, nx - 1);
    const y1 = Math.min(y0 + 1, ny - 1);
    const z1 = Math.min(z0 + 1, nz - 1);
    const tx = fx - x0;
    const ty = fy - y0;
    const tz = fz - z0;

    const i000 = ((z0 * ny + y0) * nx + x0) * 3;
    const i100 = ((z0 * ny + y0) * nx + x1) * 3;
    const i010 = ((z0 * ny + y1) * nx + x0) * 3;
    const i110 = ((z0 * ny + y1) * nx + x1) * 3;
    const i001 = ((z1 * ny + y0) * nx + x0) * 3;
    const i101 = ((z1 * ny + y0) * nx + x1) * 3;
    const i011 = ((z1 * ny + y1) * nx + x0) * 3;
    const i111 = ((z1 * ny + y1) * nx + x1) * 3;
    const data = field.data;

    for (let c = 0; c < 3; c++) {
        const c00 = data[i000 + c] * (1 - tx) + data[i100 + c] * tx;
        const c10 = data[i010 + c] * (1 - tx) + data[i110 + c] * tx;
        const c01 = data[i001 + c] * (1 - tx) + data[i101 + c] * tx;
        const c11 = data[i011 + c] * (1 - tx) + data[i111 + c] * tx;
        const c0 = c00 * (1 - ty) + c10 * ty;
        const c1 = c01 * (1 - ty) + c11 * ty;
        out[c] = c0 * (1 - tz) + c1 * tz;
    }
    return true;
}

export function dvfExtent(field: DvfGeometry): {
    min: ReadonlyVec3;
    max: ReadonlyVec3;
} {
    const last = dvfVoxelToPatient(
        field,
        field.size[0] - 1,
        field.size[1] - 1,
        field.size[2] - 1
    );
    const first = dvfVoxelToPatient(field, 0, 0, 0);
    const min = vec3.create();
    const max = vec3.create();
    vec3.min(min, first, last);
    vec3.max(max, first, last);
    return { min, max };
}

export interface DvfPlaneLattice {
    points: vec3[];
    uCount: number;
    vCount: number;
}

export function dvfPlaneLattice(
    field: DvfGeometry,
    spacingMm: number,
    planePoint: ReadonlyVec3,
    uDir: ReadonlyVec3,
    vDir: ReadonlyVec3,
    extentMin: ReadonlyVec3,
    extentMax: ReadonlyVec3
): DvfPlaneLattice {
    if (!(spacingMm > 0)) {
        throw new Error(
            `DVF lattice spacing must be positive, got ${spacingMm}`
        );
    }
    let uMin = Infinity;
    let uMax = -Infinity;
    let vMin = Infinity;
    let vMax = -Infinity;
    const corner = vec3.create();
    for (let c = 0; c < 8; c++) {
        vec3.set(
            corner,
            c & 1 ? extentMax[0] : extentMin[0],
            c & 2 ? extentMax[1] : extentMin[1],
            c & 4 ? extentMax[2] : extentMin[2]
        );
        const u = vec3.dot(corner, uDir);
        const v = vec3.dot(corner, vDir);
        uMin = Math.min(uMin, u);
        uMax = Math.max(uMax, u);
        vMin = Math.min(vMin, v);
        vMax = Math.max(vMax, v);
    }

    const anchor: ReadonlyVec3 = field.origin;
    const anchorU = vec3.dot(anchor, uDir);
    const anchorV = vec3.dot(anchor, vDir);
    const planeU = vec3.dot(planePoint, uDir);
    const planeV = vec3.dot(planePoint, vDir);

    const points: vec3[] = [];
    const kFrom = Math.ceil((uMin - anchorU) / spacingMm);
    const kTo = Math.floor((uMax - anchorU) / spacingMm);
    const lFrom = Math.ceil((vMin - anchorV) / spacingMm);
    const lTo = Math.floor((vMax - anchorV) / spacingMm);
    for (let l = lFrom; l <= lTo; l++) {
        const dv = anchorV + l * spacingMm - planeV;
        for (let k = kFrom; k <= kTo; k++) {
            const du = anchorU + k * spacingMm - planeU;
            const point = vec3.clone(planePoint as vec3);
            vec3.scaleAndAdd(point, point, uDir, du);
            vec3.scaleAndAdd(point, point, vDir, dv);
            points.push(point);
        }
    }
    return {
        points,
        uCount: Math.max(0, kTo - kFrom + 1),
        vCount: Math.max(0, lTo - lFrom + 1),
    };
}

export function dvfMagnitude(
    field: DvfField,
    x: number,
    y: number,
    z: number
): number {
    const i = flatIndex(field, x, y, z);
    const dx = field.data[i];
    const dy = field.data[i + 1];
    const dz = field.data[i + 2];
    return Math.hypot(dx, dy, dz);
}

const maxMagnitudeCache = new WeakMap<DvfField, number>();

export function dvfMaxMagnitude(field: DvfField): number {
    const cached = maxMagnitudeCache.get(field);
    if (cached !== undefined) {
        return cached;
    }
    const d = field.data;
    let max = 0;
    for (let i = 0; i < d.length; i += 3) {
        const m = Math.hypot(d[i], d[i + 1], d[i + 2]);
        if (m > max) {
            max = m;
        }
    }
    maxMagnitudeCache.set(field, max);
    return max;
}

export interface DvfJacobianStats {
    readonly foldingFraction: number;
    readonly minJacobian: number;
    readonly maxJacobian: number;
    readonly sampledVoxels: number;
}

export function dvfValidity(field: DvfField): DvfJacobianStats {
    const { metrics } = field;
    if (!metrics) {
        return dvfJacobianStats(field);
    }
    const [nx, ny, nz] = metrics.measuredSize;
    return {
        foldingFraction: metrics.foldingFraction,
        minJacobian: metrics.minJacobian,
        maxJacobian: metrics.maxJacobian,
        sampledVoxels:
            Math.max(0, nx - 2) * Math.max(0, ny - 2) * Math.max(0, nz - 2),
    };
}

export function dvfJacobianDeterminantAt(
    field: DvfField,
    x: number,
    y: number,
    z: number
): number | undefined {
    const [nx, ny, nz] = field.size;
    if (x < 1 || y < 1 || z < 1 || x >= nx - 1 || y >= ny - 1 || z >= nz - 1) {
        return undefined;
    }
    return interiorJacobianAt(field, x, y, z);
}

function interiorJacobianAt(
    field: DvfField,
    x: number,
    y: number,
    z: number
): number {
    const [nx, ny] = field.size;
    const d = field.data;
    const [sx, sy, sz] = field.spacing;
    const strideX = 3;
    const strideY = nx * 3;
    const strideZ = nx * ny * 3;
    const inv2sx = 1 / (2 * sx);
    const inv2sy = 1 / (2 * sy);
    const inv2sz = 1 / (2 * sz);
    const i = ((z * ny + y) * nx + x) * 3;

    // Gradient rows are components (u0,u1,u2); columns are axes.
    const a = 1 + (d[i + strideX] - d[i - strideX]) * inv2sx;
    const e = (d[i + strideX + 1] - d[i - strideX + 1]) * inv2sx;
    const h = (d[i + strideX + 2] - d[i - strideX + 2]) * inv2sx;
    const b = (d[i + strideY] - d[i - strideY]) * inv2sy;
    const f = 1 + (d[i + strideY + 1] - d[i - strideY + 1]) * inv2sy;
    const k = (d[i + strideY + 2] - d[i - strideY + 2]) * inv2sy;
    const c = (d[i + strideZ] - d[i - strideZ]) * inv2sz;
    const g = (d[i + strideZ + 1] - d[i - strideZ + 1]) * inv2sz;
    const l = 1 + (d[i + strideZ + 2] - d[i - strideZ + 2]) * inv2sz;

    return a * (f * l - g * k) - b * (e * l - g * h) + c * (e * k - f * h);
}

const jacobianCache = new WeakMap<DvfField, DvfJacobianStats>();

export function dvfJacobianStats(field: DvfField): DvfJacobianStats {
    const cached = jacobianCache.get(field);
    if (cached) {
        return cached;
    }

    const [nx, ny, nz] = field.size;

    let folding = 0;
    let sampled = 0;
    let minJ = Infinity;
    let maxJ = -Infinity;

    for (let z = 1; z < nz - 1; z += 1) {
        for (let y = 1; y < ny - 1; y += 1) {
            for (let x = 1; x < nx - 1; x += 1) {
                const det = interiorJacobianAt(field, x, y, z);
                sampled += 1;
                if (det <= 0) {
                    folding += 1;
                }
                if (det < minJ) {
                    minJ = det;
                }
                if (det > maxJ) {
                    maxJ = det;
                }
            }
        }
    }

    const stats: DvfJacobianStats =
        sampled === 0
            ? {
                  foldingFraction: 0,
                  minJacobian: 1,
                  maxJacobian: 1,
                  sampledVoxels: 0,
              }
            : {
                  foldingFraction: folding / sampled,
                  minJacobian: minJ,
                  maxJacobian: maxJ,
                  sampledVoxels: sampled,
              };
    jacobianCache.set(field, stats);
    return stats;
}

const meanMagnitudeCache = new WeakMap<DvfField, number>();

export function dvfMeanMagnitude(field: DvfField): number {
    const cached = meanMagnitudeCache.get(field);
    if (cached !== undefined) {
        return cached;
    }
    const d = field.data;
    let sum = 0;
    const n = Math.max(1, Math.floor(d.length / 3));
    for (let i = 0; i < d.length; i += 3) {
        sum += Math.hypot(d[i], d[i + 1], d[i + 2]);
    }
    const mean = sum / n;
    meanMagnitudeCache.set(field, mean);
    return mean;
}

export function dvfPatientToVoxel(
    field: DvfGeometry,
    point: ReadonlyVec3
): [number, number, number] | null {
    const [nx, ny, nz] = field.size;
    const x = Math.round((point[0] - field.origin[0]) / field.spacing[0]);
    const y = Math.round((point[1] - field.origin[1]) / field.spacing[1]);
    const z = Math.round((point[2] - field.origin[2]) / field.spacing[2]);
    if (x < 0 || y < 0 || z < 0 || x >= nx || y >= ny || z >= nz) {
        return null;
    }
    return [x, y, z];
}

export interface DvfProbe {
    readonly magnitudeMm: number;
    // The displacement components, in the same direction the arrows are drawn.
    readonly displacementMm: readonly [number, number, number];
    readonly jacobian: number | undefined;
}

export function dvfProbeAt(
    field: DvfField,
    point: ReadonlyVec3
): DvfProbe | undefined {
    const voxel = dvfPatientToVoxel(field, point);
    if (!voxel) {
        return undefined;
    }
    const [x, y, z] = voxel;
    // Negated to match the arrows: the stored field maps target onto source.
    const disp = dvfSampleNearest(field, x, y, z);
    return {
        magnitudeMm: dvfMagnitude(field, x, y, z),
        displacementMm: [-disp[0], -disp[1], -disp[2]],
        jacobian: dvfJacobianDeterminantAt(field, x, y, z),
    };
}
